import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { AlertMetric, AlertRule, Comparator, MacroScope, RuleDraft, Severity } from "../types";
import { scopeLabel, timeAgo } from "../format";
import { SEVERITY_LABEL, SeverityIcon, conditionText } from "./Alerts";

const EXAMPLES = [
  "Tell me when any Tablets SKU's consensus is more than 10% off the statistical forecast in the next 3 months",
  "Alert me if Laptops consensus drops more than 15% below last year, high priority",
  "Flag SKUs whose forecast error (WAPE) is above 30%",
  "Watch for consensus more than 8% above budget for Phones overall",
];

interface Props {
  boardId: string;
  boardName: string;
  macro: MacroScope;
  notify: (msg: string) => void;
  /** Rules were created, changed or checked: the canvas should reload its alerts. */
  onAlertsChanged: () => void;
}

export default function AlertConfigs({ boardId, boardName, macro, notify, onAlertsChanged }: Props) {
  const [rules, setRules] = useState<AlertRule[] | null>(null);
  const [metrics, setMetrics] = useState<AlertMetric[]>([]);
  const [interval, setIntervalS] = useState(0);
  const [text, setText] = useState("");
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => api.alertRules(boardId).then((d) => setRules(d.rules)).catch((e) => notify(e.message)),
    [boardId, notify]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.alertMetrics().then((d) => { setMetrics(d.metrics); setIntervalS(d.interval_seconds); }).catch(() => undefined);
  }, []);
  const metricById = useMemo(() => Object.fromEntries(metrics.map((m) => [m.id, m])), [metrics]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError("");
    try { await fn(); } catch (e) { setError((e as Error).message); } finally { setBusy(""); }
  };

  const understand = () => run("parse", async () => {
    const scope = { family: macro.family, group1: macro.group1, group2: macro.group2, skus: [] };
    const { draft } = await api.parseRule(boardId, text, scope);
    setDraft(draft);
  });

  const save = () => run("save", async () => {
    if (!draft) return;
    const { rule } = await api.createRule(boardId, { ...draft, threshold: Number(draft.threshold) });
    const n = rule.last_check?.new ?? 0;
    notify(n ? `Tracking “${rule.name}”: ${n} alert${n === 1 ? "" : "s"} raised now.` : `Tracking “${rule.name}”. Nothing breaches it right now.`);
    setDraft(null);
    setText("");
    load();
    onAlertsChanged();
  });

  const change = (r: AlertRule, changes: Parameters<typeof api.updateRule>[2]) => run(r.id, async () => {
    await api.updateRule(boardId, r.id, changes);
    load();
    onAlertsChanged();
  });

  const checkAll = () => run("check", async () => {
    const { rules } = await api.checkAlerts(boardId);
    const n = rules.reduce((s, r) => s + (r.last_check?.new ?? 0), 0);
    notify(n ? `${n} new alert${n === 1 ? "" : "s"}.` : "Checked: no new alerts.");
    load();
    onAlertsChanged();
  });

  return (
    <div className="alert-configs">
      <div className="alert-configs-inner">
        <div className="row">
          <h1 style={{ margin: 0, fontSize: 20 }} className="grow">Alert configs <span className="muted" style={{ fontWeight: 400 }}>· {boardName}</span></h1>
          <span className="small muted">{interval ? `Checked every ${Math.round(interval / 60)} min and after every push` : "Checked on demand"}</span>
          <button className="btn sm" disabled={!!busy || !rules?.length} onClick={checkAll}>{busy === "check" ? "Checking…" : "Check all now"}</button>
        </div>

        <div className="panel">
          <div className="panel-head"><h3>New alert</h3>
            <span className="small muted right">Scope starts from the top filters: {scopeLabel({ ...macro, skus: [] })}</span></div>
          <textarea className="input" rows={2} placeholder="Describe what gentiK should watch for…" value={text}
                    onChange={(e) => { setText(e.target.value); setDraft(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) understand(); }} />
          {!draft && (
            <div className="row" style={{ flexWrap: "wrap" }}>
              {EXAMPLES.map((ex) => (
                <button key={ex} type="button" className="chip click small" onClick={() => setText(ex)}>{ex}</button>
              ))}
            </div>
          )}
          {!draft && (
            <div className="row">
              <span className="grow" />
              <button className="btn primary sm" disabled={!text.trim() || !!busy} onClick={understand}>
                {busy === "parse" ? "Understanding…" : "Understand"}</button>
            </div>
          )}
          {draft && <DraftEditor draft={draft} metrics={metrics} onChange={setDraft} />}
          {draft && (
            <div className="row">
              <button className="btn ghost sm" onClick={() => setDraft(null)}>Back</button>
              <span className="grow" />
              <button className="btn primary sm" disabled={!!busy || !draft.metric || draft.threshold == null || metricById[draft.metric]?.available === false}
                      onClick={save}>{busy === "save" ? "Saving & checking…" : "Start tracking"}</button>
            </div>
          )}
          {error && <div className="small error-text">{error}</div>}
        </div>

        <div className="panel">
          <div className="panel-head"><h3>Tracked rules</h3><span className="small muted right">{rules?.length ?? 0}</span></div>
          {rules === null ? <div className="row small muted"><div className="spinner" /> Loading…</div>
            : rules.length === 0 ? <div className="empty">No alert rules yet. Describe one above.</div>
            : (
              <table className="data rules-table">
                <thead><tr><th /><th>Rule</th><th>Condition</th><th>Scope</th><th className="num">Open</th>
                  <th className="num">Acted</th><th>Last check</th><th>On</th><th /></tr></thead>
                <tbody>{rules.map((r) => (
                  <RuleRow key={r.id} rule={r} busy={busy === r.id}
                           onToggle={() => change(r, { enabled: !r.enabled })}
                           onThreshold={(t) => change(r, { threshold: t })}
                           onSeverity={(s) => change(r, { severity: s })}
                           onCheck={() => run(r.id, async () => { await api.checkRule(boardId, r.id); load(); onAlertsChanged(); })}
                           onDelete={() => {
                             if (!window.confirm(`Stop tracking “${r.name}” and remove its alerts?`)) return;
                             run(r.id, async () => { await api.deleteRule(boardId, r.id); load(); onAlertsChanged(); });
                           }} />
                ))}</tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

/** How gentiK understood the text: every field can be corrected before it is saved. */
function DraftEditor({ draft, metrics, onChange }: { draft: RuleDraft; metrics: AlertMetric[]; onChange: (d: RuleDraft) => void }) {
  const set = (patch: Partial<RuleDraft>) => onChange({ ...draft, ...patch });
  const m = metrics.find((x) => x.id === draft.metric);
  const p = draft.parse;
  return (
    <div className="rule-draft">
      <div className="small ink2">
        {p.method === "llm" ? "Understood by Claude" : "Understood by keyword rules"}{p.rationale ? `: ${p.rationale}` : ""}
        {p.error && <span className="muted"> (model unavailable: {p.error})</span>}
      </div>
      {draft.problems.length > 0 && (
        <ul className="small rule-problems">{draft.problems.map((x) => <li key={x}>{x}</li>)}</ul>
      )}
      <div className="rule-grid">
        <label className="field"><span>Name</span>
          <input className="input" value={draft.name} onChange={(e) => set({ name: e.target.value })} /></label>
        <label className="field"><span>Measure</span>
          <select className="input" value={draft.metric} onChange={(e) => set({ metric: e.target.value })}>
            <option value="" disabled>Pick a measure…</option>
            {metrics.map((x) => (
              <option key={x.id} value={x.id} disabled={!x.available}>{x.label}{x.available ? "" : ` (${x.unavailable_reason})`}</option>
            ))}
          </select></label>
        <label className="field"><span>Condition</span>
          <div className="row">
            <select className="input" style={{ width: 120 }} value={draft.comparator} onChange={(e) => set({ comparator: e.target.value as Comparator })}>
              <option value="above">above</option><option value="below">below</option><option value="outside">beyond ±</option>
            </select>
            <input className="input" type="number" step="0.5" style={{ width: 90 }} value={draft.threshold ?? ""}
                   onChange={(e) => set({ threshold: e.target.value === "" ? null : Number(e.target.value) })} aria-label="Threshold %" />
            <span className="small muted">%</span>
          </div></label>
        {(!m || m.timed) && (
          <label className="field"><span>Months ahead</span>
            <input className="input" type="number" min={1} max={24} value={draft.horizon_months}
                   onChange={(e) => set({ horizon_months: Math.max(1, Math.min(24, Number(e.target.value) || 1)) })} /></label>
        )}
        <label className="field"><span>Raise an alert</span>
          <select className="input" value={draft.grain} onChange={(e) => set({ grain: e.target.value as "sku" | "total" })}>
            <option value="sku">per SKU</option><option value="total">for the scope total</option>
          </select></label>
        <label className="field"><span>Severity</span>
          <select className="input" value={draft.severity} onChange={(e) => set({ severity: e.target.value as Severity })}>
            {(["high", "medium", "low"] as Severity[]).map((s) => <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>)}
          </select></label>
      </div>
      <div className="small">
        <SeverityIcon severity={draft.severity} size={12} />{" "}
        <b>{m?.label ?? "—"}</b> {conditionText(draft.comparator, draft.threshold)}
        {m && !m.timed ? " over recent closed months" : ` in the next ${draft.horizon_months} months`}
        {draft.grain === "total" ? ", scope total" : ", per SKU"} · <span className="ink2">{scopeLabel(draft.scope)}</span>
        {draft.parse.scope_resolution?.sku_count ? <span className="muted"> ({draft.parse.scope_resolution.sku_count} SKUs)</span> : null}
      </div>
      {m && <div className="small muted">{m.description}</div>}
    </div>
  );
}

function RuleRow({ rule, busy, onToggle, onThreshold, onSeverity, onCheck, onDelete }: {
  rule: AlertRule; busy: boolean; onToggle: () => void; onThreshold: (t: number) => void;
  onSeverity: (s: Severity) => void; onCheck: () => void; onDelete: () => void;
}) {
  const [t, setT] = useState(String(rule.threshold ?? ""));
  useEffect(() => setT(String(rule.threshold ?? "")), [rule.threshold]);
  const c = rule.counts;
  const open = (c?.open ?? 0) + (c?.acknowledged ?? 0);
  const commit = () => { const v = Number(t); if (t !== "" && !Number.isNaN(v) && v !== rule.threshold) onThreshold(v); };
  return (
    <tr className={rule.enabled ? "" : "paused"}>
      <td>
        <div className="row" style={{ gap: 4 }}>
          <SeverityIcon severity={rule.severity} />
          <select className="input sev-select" value={rule.severity} aria-label={`Severity of ${rule.name}`} disabled={busy}
                  onChange={(e) => onSeverity(e.target.value as Severity)}>
            {(["high", "medium", "low"] as Severity[]).map((s) => <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>)}
          </select>
        </div>
      </td>
      <td><b>{rule.name}</b>{rule.text && <div className="small muted rule-text" title={rule.text}>{rule.text}</div>}</td>
      <td className="small">
        <div>{rule.summary}</div>
        <div className="row" style={{ gap: 4, marginTop: 2 }}>
          <span className="muted">threshold</span>
          <input className="input threshold-input" type="number" step="0.5" value={t} disabled={busy}
                 onChange={(e) => setT(e.target.value)} onBlur={commit}
                 onKeyDown={(e) => { if (e.key === "Enter") commit(); }} aria-label={`Threshold for ${rule.name}`} />
          <span className="muted">%</span>
        </div>
      </td>
      <td className="small">{scopeLabel(rule.scope)}</td>
      <td className="num">{open ? <b className="open-count">{open}</b> : <span className="muted">0</span>}</td>
      <td className="num small muted">{c?.converted ?? 0}</td>
      <td className="small">
        {rule.last_checked_at ? timeAgo(rule.last_checked_at) : "never"}
        {rule.last_check?.error && <div className="error-text" title={rule.last_check.error}>check failed</div>}
      </td>
      <td><input type="checkbox" checked={rule.enabled} disabled={busy} onChange={onToggle} aria-label={`Track ${rule.name}`} /></td>
      <td className="row" style={{ justifyContent: "flex-end" }}>
        <button className="btn sm" disabled={busy || !rule.enabled} onClick={onCheck}>{busy ? "…" : "Check"}</button>
        <button className="btn ghost sm danger" disabled={busy} onClick={onDelete} aria-label={`Delete ${rule.name}`}>Delete</button>
      </td>
    </tr>
  );
}
